import { useState, useCallback } from 'react'
import { api } from '@/lib/api'
import type { ExportFormat } from '@/components/problem/ExportButtons'

export interface ExportResult {
  success: boolean
  format: ExportFormat
  content: string
  filename: string
  mimeType: string
  sizeBytes: number
}

export interface UseExportOptions {
  projectId: string
  includeConversation?: boolean
  includeTreeDiagram?: boolean
}

export interface UseExportReturn {
  isExporting: boolean
  exportingFormat: ExportFormat | null
  lastExport: ExportResult | null
  error: string | null

  exportAs: (format: ExportFormat) => Promise<ExportResult | null>
  downloadExport: (result: ExportResult) => void
  copyToClipboard: (result: ExportResult) => Promise<boolean>
  clearError: () => void
}

const mimeTypes: Record<ExportFormat, string> = {
  latex: 'application/x-latex',
  markdown: 'text/markdown',
  json: 'application/json',
  mermaid: 'text/plain',
  graphviz: 'text/plain',
}

const fileExtensions: Record<ExportFormat, string> = {
  latex: '.tex',
  markdown: '.md',
  json: '.json',
  mermaid: '.mmd',
  graphviz: '.dot',
}

export const useExport = (options: UseExportOptions): UseExportReturn => {
  const { projectId, includeConversation = true, includeTreeDiagram = true } = options

  const [isExporting, setIsExporting] = useState(false)
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null)
  const [lastExport, setLastExport] = useState<ExportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const exportAs = useCallback(
    async (format: ExportFormat): Promise<ExportResult | null> => {
      setIsExporting(true)
      setExportingFormat(format)
      setError(null)

      try {
        const response = await api.export.generate(projectId, format, {
          includeConversation,
          includeTreeDiagram,
        })

        if (!response.success || !response.data) {
          setError(response.error ?? 'Export failed')
          return null
        }

        const { content, filename: backendFilename } = response.data
        const finalFilename = backendFilename?.includes('.')
          ? backendFilename
          : `${backendFilename ?? 'project'}${fileExtensions[format]}`

        const result: ExportResult = {
          success: true,
          format,
          content,
          filename: finalFilename,
          mimeType: mimeTypes[format],
          sizeBytes: new Blob([content]).size,
        }

        setLastExport(result)
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed'
        setError(message)
        return null
      } finally {
        setIsExporting(false)
        setExportingFormat(null)
      }
    },
    [projectId, includeConversation, includeTreeDiagram]
  )

  const downloadExport = useCallback((result: ExportResult) => {
    // Create blob and download
    const blob = new Blob([result.content], { type: result.mimeType })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = result.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up
    URL.revokeObjectURL(url)
  }, [])

  const copyToClipboard = useCallback(async (result: ExportResult): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(result.content)
      return true
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      return false
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    isExporting,
    exportingFormat,
    lastExport,
    error,
    exportAs,
    downloadExport,
    copyToClipboard,
    clearError,
  }
}

export default useExport
